<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('audit-logs::page.title.audit_logs') }}</h2>

            <div>
                <b-datatable
                    :key="datatableKey"
                    :initial-sort="initialSort"
                    :columns="columns"
                    :endpoint="listRoute"
                    :hidden-columns="hiddenColumns"
                    :initial-filters="initialFilters"
                    :multi-sort="false"
                    item-key="id"
                    has-filter-selector
                    hide-buttons
                    qc-mode
                >
                    <template #filters="{ filters }">
                        <v-col cols="4" class="pb-0">
                            <b-filter-input
                                v-model="filters.filters.entity_id"
                                :label="t('audit-logs::labels.entity_id')"
                            >
                            </b-filter-input>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-select
                                v-model="filters.filters.log_type"
                                :items="filterLogTypes"
                                :label="t('audit-logs::grid.log_type')"
                                :clearable="filters.filters.log_type !== null"
                                item-text="label"
                                item-value="value"
                            >
                            </b-filter-select>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-select
                                v-model="filters.filters.action_type"
                                :items="filterActionTypes"
                                :label="t('audit-logs::grid.action_type')"
                                :clearable="filters.filters.action_type !== null"
                            >
                            </b-filter-select>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-date
                                v-model="filters.filters.filter_created_at"
                                range
                                :label="t('audit-logs::filters.label.date')"
                                @clear="onCloseDateFilter(filters, 'filter_created_at')"
                            ></b-filter-date>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <v-autocomplete
                                v-model="filters.filters.acl_route"
                                :items="locations"
                                :label="t('audit-logs::filters.page')"
                                :clearable="filters.filters.acl_route !== null"
                                dense
                                append-icon="mdi-chevron-down"
                                item-text="full_name"
                                item-value="id"
                                outlined
                                rounded
                                tabindex="4"
                            ></v-autocomplete>
                        </v-col>
                    </template>
                    <template #[`item.user.name`]="{ item }">
                        {{ item.user ? item.user.name : '-' }}
                    </template>
                    <template #[`item.created_at`]="{ item }">
                        {{ moment(item.created_at).format('DD.MM.YYYY hh:mm A') }}
                    </template>
                    <template #[`item.log_type`]="{ item }">
                        {{ t('audit-logs::labels.log_type.' + item.log_type) }}
                    </template>
                    <template #[`item.action_type`]="{ item }">
                        {{ t('audit-logs::labels.action_type.' + item.action_type) }}
                    </template>

                    <template #[`item.error_message`]="{ item }">
                        <div v-if="item.error_message">
                            <v-icon color="red">mdi-alert-octagon-outline</v-icon>
                            <span class="error--text">{{ item.error_message }}</span>
                        </div>
                    </template>

                    <template #[`item.actions`]="{ item }">
                        <div class="table-actions">
                            <v-tooltip v-if="can('audit-logs.export-record-as-pdf')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="exportRecordAsPdf(item)"
                                    >
                                        <v-icon size="24">mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.export_record_as_pdf') }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                </b-datatable>
            </div>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            hiddenColumns: [],
            locations: [],
            downloadingFile: false,
            initialFilters: {
                log_type: null,
                action_type: null,
                acl_route: null,
            },
            filterLogTypes: [
                { label: t('patients::status.all'), value: null },
                { label: t('audit-logs::labels.log_type.audit'), value: 'audit' },
                { label: t('audit-logs::labels.log_type.system'), value: 'system' },
            ],
            filterActionTypes: [],
            initialSort: '-created_at',
            columns: [
                {
                    text: t('audit-logs::grid.label.log_type'),
                    value: 'log_type',
                    sortable: false,
                    width: 100,
                },
                {
                    text: t('audit-logs::grid.label.created_at'),
                    value: 'created_at',
                    sortable: false,
                    width: 200,
                },
                {
                    text: t('audit-logs::grid.label.user'),
                    value: 'user.name',
                    sortable: false,
                    width: 200,
                },
                {
                    text: t('audit-logs::grid.label.action_type'),
                    value: 'action_type',
                    sortable: false,
                    width: 140,
                },
                {
                    text: t('audit-logs::grid.label.action'),
                    value: 'action',
                    fields: 'action',
                    sortable: false,
                    width: 300,
                },
                {
                    text: t('audit-logs::grid.label.entity_id'),
                    value: 'base_entity_id',
                    sortable: false,
                    width: 310,
                },
                {
                    text: null,
                    value: 'error_message',
                    sortable: false,
                    class: 'is_patient_mode',
                    cellClass: 'is_patient_mode',
                    width: 0,
                },
                {
                    text: t('audit-logs::grid.label.entity_name'),
                    value: 'base_entity_name',
                    sortable: false,
                    width: 150,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'id',
                    align: 'end',
                    width: 80,
                },
            ],
        };
    },
    watch: {},
    created() {
        this.loadLocations();

        axios.get(route('api.v1.audit-logs.config')).then((response) => {
            this.filterActionTypes = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.action_types
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('audit-logs.export-record-as-pdf', { audit_log: item.id }), {
                    params: {
                        fields: '*,changes',
                    },
                })
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'filter_created_at':
                    filters.filters.created_at = null;
                    filters.filters.filter_created_at = null;
                    break;
                default:
                    break;
            }
        },
        editItemUrl(item) {
            return route('audit-logs.get', { audit_log: item.id });
        },
        loadLocations() {
            axios
                .get(
                    route('api.v1.acl-routes.options', {
                        sort: 'full_name',
                        audit_disabled: 0,
                        allowed_routes: 'any',
                        // allowed_for_all: 0
                        // visible_permission: 1,
                    })
                )
                .then((response) => {
                    this.locations = _.union(
                        [
                            {
                                full_name: t('patients::status.all'),
                                id: null,
                            },
                        ],
                        response.data.data
                    );
                });
        },
    },
};
</script>
